import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { usePreferences } from '../contexts/PreferencesContext';
import {
  Box,
  Paper,
  Typography,
  Button,
  IconButton,
  Chip,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Autocomplete,
  Tooltip,
  Alert,
} from '@mui/material';
import {
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Today as TodayIcon,
  Add as AddIcon,
  CheckCircle as CheckCircleIcon,
  Delete as DeleteIcon,
  StarRounded as StarIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import { format, startOfWeek, addDays, addWeeks, subWeeks, isToday } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  getDispatchAssignments,
  getDispatchTechnicians,
  createDispatchAssignment,
  updateDispatchAssignment,
  deleteDispatchAssignment,
  sendDispatchEmail,
  searchDispatchTargets,
  searchDispatchAll,
} from '../services/api';

// ─── Constants ────────────────────────────────────────────────────────────────

const TIME_SLOT_OPTIONS = [
  'All Day',
  'Morning (7AM-12PM)',
  'Afternoon (12PM-5PM)',
  'Evening (5PM-9PM)',
];

const STATUS_OPTIONS = ['scheduled', 'confirmed', 'completed', 'cancelled'];

const STATUS_COLORS = {
  confirmed:  { bg: '#e8f5e9', border: '#2e7d32', text: '#1b5e20' },
  scheduled:  { bg: '#fffde7', border: '#f9a825', text: '#7a5c00' },
  completed:  { bg: '#f5f5f5', border: '#9e9e9e', text: '#424242' },
  cancelled:  { bg: '#ffebee', border: '#c62828', text: '#b71c1c' },
};

// Department metadata — order here matches backend DEPARTMENTS array
const DEPARTMENT_META = {
  'AfterCare':          { color: '#FF8F00' },
  'Installation':       { color: '#1565C0' },
  'Lighting & Shades':  { color: '#7B1FA2' },
  'IT':                 { color: '#00838F' },
  'Programming':        { color: '#283593' },
  'Project Managers':   { color: '#2E7D32' },
  'CAD & Engineering':  { color: '#4E342E' },
};

// Monday-based week days shown in the grid (Mon–Sat)
const WEEK_DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

// ─── Helper utilities ─────────────────────────────────────────────────────────

const getWeekStart = (date) =>
  startOfWeek(date, { weekStartsOn: 1 }); // Monday

const getWeekDates = (weekStart) =>
  WEEK_DAYS.map((_, i) => addDays(weekStart, i));

const toDateString = (date) => format(date, 'yyyy-MM-dd');

/** Hex color → rgba with given opacity */
const hexAlpha = (hex, alpha) => {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r},${g},${b},${alpha})`;
};

// ─── Assignment chip component ────────────────────────────────────────────────

const AssignmentChip = ({ assignment, onEdit, onConfirm }) => {
  const colors = STATUS_COLORS[assignment.status] || STATUS_COLORS.scheduled;
  // Tile prefix: Job# (project) or AR# + client name (AfterCare)
  const isAftercare = !assignment.job_number && !!assignment.ar_number;
  const prefix = isAftercare
    ? [assignment.ar_number, assignment.ar_client_name].filter(Boolean).join(' · ')
    : (assignment.job_number || '');
  const desc = assignment.job_description || assignment.project_name || 'Assignment';
  const label = prefix ? `${prefix} · ${desc}` : desc;
  const truncated = label.length > 28 ? label.slice(0, 26) + '…' : label;

  const tooltipContent = (
    <Box sx={{ p: 0.5, maxWidth: 280 }}>
      <Typography variant="subtitle2" fontWeight="bold">{desc}</Typography>
      {assignment.project_name && (
        <Typography variant="caption" display="block">Project: {assignment.project_name}</Typography>
      )}
      {assignment.job_number && (
        <Typography variant="caption" display="block">Job #: {assignment.job_number}</Typography>
      )}
      {assignment.ar_number && (
        <Typography variant="caption" display="block">
          AR #: {assignment.ar_number}
          {assignment.ar_client_name ? ` — ${assignment.ar_client_name}` : ''}
        </Typography>
      )}
      <Typography variant="caption" display="block">Time: {assignment.time_slot}</Typography>
      <Typography variant="caption" display="block">Status: {assignment.status}</Typography>
      {assignment.location && (
        <Typography variant="caption" display="block">Location: {assignment.location}</Typography>
      )}
      {assignment.notes && (
        <Typography variant="caption" display="block" sx={{ mt: 0.5, fontStyle: 'italic' }}>
          {assignment.notes}
        </Typography>
      )}
    </Box>
  );

  return (
    <Tooltip title={tooltipContent} placement="top" arrow>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.25,
          bgcolor: colors.bg,
          border: `1px solid ${colors.border}`,
          borderRadius: 1,
          px: 0.75,
          py: 0.25,
          mb: 0.4,
          cursor: 'pointer',
          '&:hover': { opacity: 0.85 },
          minWidth: 0,
        }}
        onClick={(e) => { e.stopPropagation(); onEdit(assignment); }}
      >
        <Typography
          variant="caption"
          sx={{ color: colors.text, fontWeight: 500, flexGrow: 1, lineHeight: 1.3, fontSize: '0.7rem' }}
        >
          {truncated}
          {assignment.time_slot && assignment.time_slot !== 'All Day' && (
            <span style={{ opacity: 0.75, marginLeft: 2 }}>
              {assignment.time_slot.replace('Morning (7AM-12PM)', '↑').replace('Afternoon (12PM-5PM)', '↓').replace('Evening (5PM-9PM)', '🌙')}
            </span>
          )}
        </Typography>
        {assignment.status !== 'confirmed' && assignment.status !== 'completed' && (
          <Tooltip title="Mark confirmed">
            <IconButton
              size="small"
              sx={{ p: 0, ml: 0.25, color: '#2e7d32' }}
              onClick={(e) => { e.stopPropagation(); onConfirm(assignment); }}
            >
              <CheckCircleIcon sx={{ fontSize: 13 }} />
            </IconButton>
          </Tooltip>
        )}
      </Box>
    </Tooltip>
  );
};

// ─── Assignment Dialog ─────────────────────────────────────────────────────────

const AssignmentDialog = ({
  open,
  onClose,
  onSave,
  onDelete,
  initialData,
  technicians,
  prefillTech,
  prefillDate,
  prefillTarget, // { kind, project_id, job_number, ar_number, client_name, project_name, site_address, label, sublabel } — optional
}) => {
  const isEdit = !!initialData?.id;

  const blank = {
    technician_id: prefillTech?.id || '',
    assignment_date: prefillDate ? toDateString(prefillDate) : toDateString(new Date()),
    project_id: '',
    job_number: '',
    ar_number: '',
    ar_client_name: '',
    job_description: '',
    time_slot: 'All Day',
    status: 'scheduled',
    location: '',
    notes: '',
  };

  const [form, setForm] = useState(blank);
  const [targetSearch, setTargetSearch] = useState('');
  const [targetOptions, setTargetOptions] = useState([]);
  const [selectedTarget, setSelectedTarget] = useState(null);
  const [targetLoading, setTargetLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // Reset form when dialog opens
  useEffect(() => {
    if (open) {
      if (isEdit && initialData) {
        setForm({
          technician_id: initialData.technician_id || '',
          assignment_date: initialData.assignment_date
            ? initialData.assignment_date.slice(0, 10)
            : toDateString(new Date()),
          project_id: initialData.project_id || '',
          job_number: initialData.job_number || '',
          ar_number: initialData.ar_number || '',
          ar_client_name: initialData.ar_client_name || '',
          job_description: initialData.job_description || '',
          time_slot: initialData.time_slot || 'All Day',
          status: initialData.status || 'scheduled',
          location: initialData.location || '',
          notes: initialData.notes || '',
        });
        // Reconstruct a synthetic selectedTarget for the chip if we have one
        if (initialData.project_id && initialData.project_name) {
          const synth = {
            kind: 'job',
            id: `job:${initialData.project_id}`,
            project_id: initialData.project_id,
            job_number: initialData.job_number || null,
            ar_number: initialData.ar_number || null,
            client_name: initialData.client_name || initialData.project_name,
            project_name: initialData.project_name,
            label: [initialData.job_number, initialData.client_name || initialData.project_name].filter(Boolean).join(' — '),
            sublabel: initialData.location || '',
            site_address: initialData.location || '',
          };
          setTargetOptions([synth]);
          setSelectedTarget(synth);
        } else if (initialData.ar_number) {
          const synth = {
            kind: 'aftercare',
            id: `ac:${initialData.ar_number}:edit`,
            project_id: null,
            job_number: null,
            ar_number: initialData.ar_number,
            client_name: initialData.ar_client_name || '',
            label: [initialData.ar_number, initialData.ar_client_name].filter(Boolean).join(' — '),
            sublabel: initialData.location || '',
            site_address: initialData.location || '',
          };
          setTargetOptions([synth]);
          setSelectedTarget(synth);
        } else {
          setTargetOptions([]);
          setSelectedTarget(null);
        }
      } else if (prefillTarget) {
        // New assignment, but with a project/AfterCare target already
        // chosen by the caller (e.g. the calendar header search bar).
        // Mirror what handleTargetChange does when the user picks a
        // target from inside the dialog — including seeding
        // job_description, because handleSave rejects an empty one
        // (Tom Clancy 2026-08-06: 'the ad assignment when we're
        // searching for a client is not taking').
        const defaultDesc = prefillTarget.kind === 'aftercare'
          ? (prefillTarget.client_name || '')
          : (prefillTarget.client_name || prefillTarget.project_name || '');
        const seedForm = {
          ...blank,
          technician_id: prefillTech?.id || '',
          assignment_date: prefillDate ? toDateString(prefillDate) : toDateString(new Date()),
          project_id: prefillTarget.kind === 'aftercare' ? '' : (prefillTarget.project_id || ''),
          job_number: prefillTarget.kind === 'aftercare' ? '' : (prefillTarget.job_number || ''),
          ar_number: prefillTarget.ar_number || '',
          ar_client_name: prefillTarget.kind === 'aftercare' ? (prefillTarget.client_name || '') : (prefillTarget.ar_number ? (prefillTarget.client_name || '') : ''),
          job_description: defaultDesc,
          location: prefillTarget.site_address || prefillTarget.sublabel || '',
        };
        setForm(seedForm);
        setTargetOptions([prefillTarget]);
        setSelectedTarget(prefillTarget);
      } else {
        setForm({ ...blank, technician_id: prefillTech?.id || '', assignment_date: prefillDate ? toDateString(prefillDate) : toDateString(new Date()) });
        setTargetOptions([]);
        setSelectedTarget(null);
      }
      setError('');
      setTargetSearch('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, isEdit]);

  // Debounced combined Job + AfterCare search
  useEffect(() => {
    if (!targetSearch || targetSearch.length < 2) return;
    const timer = setTimeout(async () => {
      setTargetLoading(true);
      try {
        const res = await searchDispatchTargets(targetSearch, 30);
        const jobs = res.data?.jobs || [];
        const aftercare = res.data?.aftercare || [];
        // Merge: jobs first (most common), then AfterCare
        setTargetOptions([...jobs, ...aftercare]);
      } catch {
        // ignore
      } finally {
        setTargetLoading(false);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [targetSearch]);

  const handleChange = (field) => (e) => {
    setForm((f) => ({ ...f, [field]: e.target.value }));
  };

  const handleTargetChange = (_, value) => {
    setSelectedTarget(value || null);
    if (!value) {
      setForm((f) => ({
        ...f,
        project_id: '',
        job_number: '',
        ar_number: '',
        ar_client_name: '',
        location: '',
      }));
      return;
    }
    // Build location string from address parts
    const locParts = [value.site_address, value.site_city, value.site_state].filter((x) => x && String(x).trim());
    const locStr = locParts.join(', ');

    if (value.kind === 'job') {
      setForm((f) => ({
        ...f,
        project_id: value.project_id || '',
        job_number: value.job_number || '',
        ar_number: value.ar_number || '',                // job MAY also have AR
        ar_client_name: value.ar_number ? (value.client_name || '') : '',
        location: locStr || f.location,
        job_description: f.job_description || value.client_name || value.project_name || '',
      }));
    } else if (value.kind === 'aftercare') {
      setForm((f) => ({
        ...f,
        project_id: '',
        job_number: '',                                  // AfterCare — no job#
        ar_number: value.ar_number || '',
        ar_client_name: value.client_name || '',
        location: locStr || f.location,
        job_description: f.job_description || value.client_name || '',
      }));
    }
  };

  const handleSave = async () => {
    if (!form.job_description.trim()) {
      setError('Job description is required.');
      return;
    }
    if (!form.assignment_date) {
      setError('Date is required.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      await onSave(form, isEdit ? initialData.id : null);
      onClose();
    } catch (err) {
      setError(err?.response?.data?.error || 'Failed to save assignment.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Delete this assignment?')) return;
    setSaving(true);
    try {
      await onDelete(initialData.id);
      onClose();
    } catch {
      setError('Failed to delete assignment.');
    } finally {
      setSaving(false);
    }
  };

  const selectedTech = technicians.find((t) => t.id === form.technician_id) || null;

  // If a target was pre-selected (e.g. via the calendar-header search),
  // show a clear "you're assigning THIS job — now pick tech + day" banner
  // at the top of the dialog. (Tom Clancy 2026-08-06.)
  const showPrefillBanner = !isEdit && !!prefillTarget && !!selectedTarget;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>
        {isEdit
          ? 'Edit Assignment'
          : showPrefillBanner
            ? `Assign: ${selectedTarget.label}`
            : 'Add Assignment'}
      </DialogTitle>
      <DialogContent dividers sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
        {error && <Alert severity="error">{error}</Alert>}

        {showPrefillBanner && (
          <Alert severity="info" icon={false} sx={{ py: 0.75 }}>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontWeight: 600, letterSpacing: 0.5 }}>
              JOB SELECTED
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {selectedTarget.label}
            </Typography>
            {selectedTarget.site_address && (
              <Typography variant="body2" sx={{ mt: 0.25 }}>
                📍 {selectedTarget.site_address}
              </Typography>
            )}
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
              Now pick a technician and a day below.
            </Typography>
          </Alert>
        )}

        {/* Technician */}
        <Autocomplete
          options={technicians}
          getOptionLabel={(t) => `${t.name} (${t.role})`}
          value={selectedTech}
          onChange={(_, v) => setForm((f) => ({ ...f, technician_id: v?.id || '' }))}
          renderInput={(params) => (
            <TextField {...params} label="Technician" size="small" />
          )}
        />

        {/* Date */}
        <TextField
          label="Date"
          type="date"
          value={form.assignment_date}
          onChange={handleChange('assignment_date')}
          size="small"
          slotProps={{ inputLabel: { shrink: true } }}
        />

        {/* Combined Project / AfterCare target search */}
        <Autocomplete
          options={targetOptions}
          getOptionLabel={(o) => o?.label || ''}
          isOptionEqualToValue={(a, b) => a?.id === b?.id}
          value={selectedTarget}
          onChange={handleTargetChange}
          onInputChange={(_, v, reason) => { if (reason !== 'reset') setTargetSearch(v); }}
          loading={targetLoading}
          filterOptions={(x) => x}
          groupBy={(o) => (o.kind === 'job' ? 'Active Projects' : 'AfterCare Clients')}
          renderOption={(props, option) => {
            // React 19 + MUI v9: `key` lives inside `props` and must be passed
            // explicitly to the rendered <li> (spreading it does nothing).
            // Without this the option list silently fails to render — the
            // exact symptom Tom hit on 2026-05-28 (search returns 0 visible
            // results even when the API is happily returning matches).
            const { key, ...optionProps } = props;
            return (
            <Box component="li" key={key} {...optionProps} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, py: 1 }}>
              <Chip
                size="small"
                label={option.kind === 'job' ? 'JOB' : 'AC'}
                sx={{
                  fontSize: '0.65rem',
                  fontWeight: 700,
                  height: 20,
                  bgcolor: option.kind === 'job' ? '#1565C0' : '#FF8F00',
                  color: '#fff',
                  mt: 0.25,
                  flexShrink: 0,
                }}
              />
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.3 }}>
                  {option.label}
                </Typography>
                {option.sublabel && (
                  <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', lineHeight: 1.3 }}>
                    {option.sublabel}
                  </Typography>
                )}
              </Box>
            </Box>
            );
          }}
          renderInput={(params) => (
            // MUI v9 + React 19: do NOT override `slotProps` here. The
            // Autocomplete's `params` already carries slotProps.input,
            // slotProps.htmlInput, slotProps.inputLabel — those wire up
            // the input's ref, onChange, value, popper anchoring, etc.
            // Setting `slotProps={{ input: {...} }}` after `{...params}`
            // wipes `slotProps.htmlInput`, which kills the onChange handler
            // and the popper anchor. Symptom: typing does nothing, no API
            // call, no dropdown (Tom 2026-05-28). The Autocomplete `loading`
            // prop renders the spinner natively — no manual endAdornment
            // override needed.
            <TextField
              {...params}
              label="Project / AfterCare Client"
              size="small"
              placeholder="Type a job#, AR#, client name, or address…"
              // Disable Chrome's address autofill — it was intercepting the
              // popper and offering "Fill address" suggestions (Tom 2026-05-28).
              // Chrome ignores autoComplete="off" on text inputs, so we use a
              // bogus token, which Chrome treats as an unrecognized field and
              // skips autofill on.
              slotProps={{
                ...(params.slotProps || {}),
                htmlInput: {
                  ...(params.slotProps?.htmlInput || {}),
                  autoComplete: 'new-password',
                },
              }}
            />
          )}
        />

        {/* Job description */}
        <TextField
          label="Job Description"
          value={form.job_description}
          onChange={handleChange('job_description')}
          size="small"
          required
          placeholder="e.g. 220cps all day, Barnett 68E86"
        />

        {/* Job # + AR # side by side */}
        <Box sx={{ display: 'flex', gap: 2 }}>
          <TextField
            label="Job Number"
            value={form.job_number}
            onChange={handleChange('job_number')}
            size="small"
            placeholder="e.g. 68E86"
            sx={{ flex: 1 }}
          />
          <TextField
            label="AR Number"
            value={form.ar_number}
            onChange={handleChange('ar_number')}
            size="small"
            placeholder="e.g. SMITHJ001"
            sx={{ flex: 1 }}
          />
        </Box>

        <Box sx={{ display: 'flex', gap: 2 }}>
          {/* Time slot */}
          <FormControl size="small" sx={{ flex: 1 }}>
            <InputLabel>Time Slot</InputLabel>
            <Select value={form.time_slot} onChange={handleChange('time_slot')} label="Time Slot">
              {TIME_SLOT_OPTIONS.map((t) => (
                <MenuItem key={t} value={t}>{t}</MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Status */}
          <FormControl size="small" sx={{ flex: 1 }}>
            <InputLabel>Status</InputLabel>
            <Select value={form.status} onChange={handleChange('status')} label="Status">
              {STATUS_OPTIONS.map((s) => (
                <MenuItem key={s} value={s} sx={{ textTransform: 'capitalize' }}>{s}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        {/* Location */}
        <TextField
          label="Location"
          value={form.location}
          onChange={handleChange('location')}
          size="small"
          placeholder="Site / address"
        />

        {/* Notes */}
        <TextField
          label="Notes"
          value={form.notes}
          onChange={handleChange('notes')}
          size="small"
          multiline
          rows={2}
        />
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2, justifyContent: 'space-between' }}>
        <Box>
          {isEdit && (
            <Button
              color="error"
              startIcon={<DeleteIcon />}
              onClick={handleDelete}
              disabled={saving}
              size="small"
            >
              Delete
            </Button>
          )}
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button onClick={onClose} disabled={saving}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}>
            {saving ? <CircularProgress size={18} /> : isEdit ? 'Save Changes' : 'Add Assignment'}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

// ─── Department toggle chip ────────────────────────────────────────────────────

const DeptToggle = ({ name, color, visible, onToggle }) => (
  <Chip
    label={name}
    size="small"
    onClick={() => onToggle(name)}
    sx={{
      fontWeight: 600,
      fontSize: '0.72rem',
      bgcolor: visible ? color : 'transparent',
      color: visible ? '#fff' : color,
      border: `1.5px solid ${color}`,
      '&:hover': {
        bgcolor: visible ? color : hexAlpha(color, 0.12),
      },
      transition: 'all 0.15s',
    }}
  />
);

// ─── Main Dispatch component ───────────────────────────────────────────────────

const DISPATCH_EMAIL_IDS = new Set([3, 34, 6, 55]); // Tom, Andy W, Mike G, Gerson

const Dispatch = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { preferences, loaded: prefsLoaded } = usePreferences();
  const [weekStart, setWeekStart] = useState(() => getWeekStart(new Date()));
  const [assignments, setAssignments] = useState([]);
  const [technicians, setTechnicians] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [sendingEmail, setSendingEmail] = useState(false);

  // Department visibility toggles — initialized from preferences once loaded
  const prefsInitialized = useRef(false);
  const [hiddenDepts, setHiddenDepts] = useState(new Set());

  // Once preferences are loaded, initialize hiddenDepts from saved prefs (only once)
  useEffect(() => {
    if (prefsLoaded && !prefsInitialized.current) {
      prefsInitialized.current = true;
      const saved = preferences?.dispatch?.hiddenDepartments || [];
      if (saved.length > 0) {
        setHiddenDepts(new Set(saved));
      }
    }
  }, [prefsLoaded, preferences]);

  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingAssignment, setEditingAssignment] = useState(null);
  const [prefillTarget, setPrefillTarget] = useState(null); // { kind, project_id, ... } from calendar-header search

  // ── Calendar header search (Tom Clancy 2026-08-06) ─────────────────────
  // Comprehensive search across EVERY project ever + all AfterCare sites.
  // Unlike the dialog's own picker (which is WIP-only), this hits closed,
  // cancelled, and legacy jobs so dispatch can schedule against anything.
  const [globalSearch, setGlobalSearch] = useState('');
  const [globalSearchOpts, setGlobalSearchOpts] = useState([]);
  const [globalSearching, setGlobalSearching] = useState(false);
  const [globalSearchOpen, setGlobalSearchOpen] = useState(false);
  useEffect(() => {
    const q = (globalSearch || '').trim();
    if (q.length < 2) { setGlobalSearchOpts([]); return; }
    let cancelled = false;
    const timer = setTimeout(async () => {
      setGlobalSearching(true);
      try {
        const res = await searchDispatchAll(q, 60);
        if (cancelled) return;
        const jobs = res.data?.jobs || [];
        const aftercare = res.data?.aftercare || [];
        setGlobalSearchOpts([...jobs, ...aftercare]);
      } catch {
        if (!cancelled) setGlobalSearchOpts([]);
      } finally {
        if (!cancelled) setGlobalSearching(false);
      }
    }, 300);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [globalSearch]);
  const [prefillTech, setPrefillTech] = useState(null);
  const [prefillDate, setPrefillDate] = useState(null);

  // Memoize weekDates so the array reference is stable between renders
  const weekDates = useMemo(() => getWeekDates(weekStart), [weekStart]);

  // ── Load data ────────────────────────────────────────────────────────────────

  const loadTechnicians = useCallback(async () => {
    try {
      const res = await getDispatchTechnicians();
      setTechnicians(res.data);
    } catch (err) {
      console.error('Failed to load technicians:', err);
    }
  }, []);

  const loadAssignments = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const startStr = toDateString(weekStart);
      const endStr = toDateString(addDays(weekStart, WEEK_DAYS.length - 1));
      const res = await getDispatchAssignments(startStr, endStr);
      setAssignments(res.data);
    } catch (err) {
      console.error('Failed to load dispatch assignments:', err);
      setError('Failed to load assignments. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [weekStart]);

  useEffect(() => {
    loadTechnicians();
  }, [loadTechnicians]);

  useEffect(() => {
    loadAssignments();
  }, [loadAssignments]);

  // ── Week navigation ──────────────────────────────────────────────────────────

  const goToPrevWeek = () => setWeekStart((w) => getWeekStart(subWeeks(w, 1)));
  const goToNextWeek = () => setWeekStart((w) => getWeekStart(addWeeks(w, 1)));
  const goToThisWeek = () => setWeekStart(getWeekStart(new Date()));

  // ── Department toggle ────────────────────────────────────────────────────────

  const toggleDept = (deptName) => {
    setHiddenDepts((prev) => {
      const next = new Set(prev);
      if (next.has(deptName)) next.delete(deptName);
      else next.add(deptName);
      return next;
    });
  };

  // ── Dialog helpers ───────────────────────────────────────────────────────────

  const openAddDialog = (tech, date, target = null) => {
    setEditingAssignment(null);
    setPrefillTech(tech);
    setPrefillDate(date);
    setPrefillTarget(target);
    setDialogOpen(true);
  };

  const openEditDialog = (assignment) => {
    setEditingAssignment(assignment);
    setPrefillTech(null);
    setPrefillDate(null);
    setPrefillTarget(null);
    setDialogOpen(true);
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setEditingAssignment(null);
    setPrefillTarget(null);
  };

  const handlePickGlobalSearchOption = (opt) => {
    if (!opt) return;
    // Open the Add Assignment dialog with this target prefilled.
    // Neither tech nor date pre-selected — dispatcher explicitly picks
    // both in the dialog (Tom Clancy 2026-08-06: 'assign to a technician
    // as well as a day'). The dialog defaults date to today if left
    // empty on save.
    openAddDialog(null, null, opt);
    setGlobalSearch('');
    setGlobalSearchOpts([]);
    setGlobalSearchOpen(false);
  };

  // ── CRUD operations ──────────────────────────────────────────────────────────

  // Parse an assignment_date field from the API safely. The DB column
  // is `date`, but pg serializes it as an ISO string with a timestamp
  // ('2026-08-07T00:00:00.000Z'). Concatenating 'T12:00:00' onto that
  // yields an Invalid Date and everything downstream throws — which
  // is exactly what caused a 'Failed to save assignment' toast even
  // though the row was created (Tom Clancy 2026-08-06).
  const parseAssignmentDate = (raw) => {
    if (!raw) return null;
    const ymd = String(raw).slice(0, 10); // 'YYYY-MM-DD'
    if (!/^\d{4}-\d{2}-\d{2}$/.test(ymd)) return null;
    const d = new Date(ymd + 'T12:00:00');
    return isNaN(d.getTime()) ? null : d;
  };

  const jumpWeekIfNeeded = (rawDate) => {
    const newDate = parseAssignmentDate(rawDate);
    if (!newDate) return;
    const targetWeekStart = getWeekStart(newDate);
    if (toDateString(targetWeekStart) !== toDateString(weekStart)) {
      setWeekStart(targetWeekStart);
    }
  };

  const handleSave = async (formData, editId) => {
    if (editId) {
      const res = await updateDispatchAssignment(editId, formData);
      setAssignments((prev) => prev.map((a) => (a.id === editId ? res.data : a)));
      jumpWeekIfNeeded(res.data?.assignment_date);
    } else {
      const res = await createDispatchAssignment(formData);
      setAssignments((prev) => [...prev, res.data]);
      // Tom Clancy (2026-08-06): if the new assignment falls outside
      // the currently-visible week (e.g. dispatcher searched a client,
      // picked next Thursday), jump the calendar to that week so the
      // assignment is actually visible — otherwise it looks like the
      // save 'didn't take'.
      jumpWeekIfNeeded(res.data?.assignment_date);
    }
  };

  const handleDelete = async (id) => {
    await deleteDispatchAssignment(id);
    setAssignments((prev) => prev.filter((a) => a.id !== id));
  };

  const handleConfirm = async (assignment) => {
    try {
      const res = await updateDispatchAssignment(assignment.id, { status: 'confirmed' });
      setAssignments((prev) => prev.map((a) => (a.id === assignment.id ? res.data : a)));
    } catch (err) {
      console.error('Failed to confirm assignment:', err);
    }
  };

  // ── Send dispatch email ──────────────────────────────────────────────────────

  const handleSendEmail = async () => {
    // Pick next weekday from current view
    const dates = getWeekDates(weekStart).filter(d => d.getDay() >= 1 && d.getDay() <= 5);
    const nextDate = dates.find(d => d >= new Date()) || dates[0];
    const dateStr = format(nextDate, 'yyyy-MM-dd');
    const label = format(nextDate, 'EEEE, MMMM d');

    if (!window.confirm(`Send dispatch email for ${label} to Tom, Mike Grady & Andy W? (test mode)`)) return;

    setSendingEmail(true);
    try {
      const res = await sendDispatchEmail(dateStr);
      alert(`Dispatch email sent for ${label} (${res.data.assignments} assignments)`);
    } catch (err) {
      alert('Failed to send: ' + (err.response?.data?.error || err.message));
    } finally {
      setSendingEmail(false);
    }
  };

  // ── Build grouped technicians ────────────────────────────────────────────────

  // Group techs by department (they arrive pre-sorted from the server)
  const groupedDepts = useMemo(() => {
    const map = new Map();
    technicians.forEach((t) => {
      const dept = t.department || 'Other';
      if (!map.has(dept)) map.set(dept, []);
      map.get(dept).push(t);
    });
    // Convert to ordered array using DEPARTMENT_META key order
    const orderedNames = Object.keys(DEPARTMENT_META);
    const result = [];
    orderedNames.forEach((deptName) => {
      if (map.has(deptName)) {
        result.push({ name: deptName, techs: map.get(deptName) });
      }
    });
    // Any remaining depts not in DEPARTMENT_META go at the end
    map.forEach((techs, deptName) => {
      if (!orderedNames.includes(deptName)) {
        result.push({ name: deptName, techs });
      }
    });
    return result;
  }, [technicians]);

  // Build lookup: Map<techId, Map<dateStr, assignment[]>>
  const assignmentMap = useMemo(() => {
    const map = new Map();
    assignments.forEach((a) => {
      const techKey = a.technician_id || `name:${a.technician_name}`;
      const dateKey = a.assignment_date ? a.assignment_date.slice(0, 10) : '';
      if (!map.has(techKey)) map.set(techKey, new Map());
      const dateMap = map.get(techKey);
      if (!dateMap.has(dateKey)) dateMap.set(dateKey, []);
      dateMap.get(dateKey).push(a);
    });
    return map;
  }, [assignments]);

  const getAssignmentsForCell = (techId, date) => {
    const dateKey = toDateString(date);
    return assignmentMap.get(techId)?.get(dateKey) || [];
  };

  // ── Render ───────────────────────────────────────────────────────────────────

  const headerLabel = `Week of ${format(weekStart, 'MMMM d, yyyy')}`;
  const allDeptNames = Object.keys(DEPARTMENT_META);

  return (
    <Box>
      {/* ── Page header ── */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 1 }}>
        <Typography variant="h5" fontWeight="bold">
          Dispatch Calendar
        </Typography>

        {/* Comprehensive project + AfterCare search (Tom Clancy 2026-08-06).
            Searches every project ever — active, closed, cancelled,
            on-hold — plus every AfterCare service site. Picking a
            result opens Add Assignment prefilled with that target. */}
        <Box sx={{ flex: 1, maxWidth: 520, minWidth: 260, mx: 2 }}>
          <Autocomplete
            size="small"
            freeSolo
            fullWidth
            open={globalSearchOpen && globalSearch.trim().length >= 2}
            onOpen={() => setGlobalSearchOpen(true)}
            onClose={() => setGlobalSearchOpen(false)}
            options={globalSearchOpts}
            loading={globalSearching}
            filterOptions={(x) => x}
            inputValue={globalSearch}
            onInputChange={(_, v, reason) => {
              if (reason === 'reset') return;
              setGlobalSearch(v);
            }}
            onChange={(_, v) => { if (v && typeof v === 'object') handlePickGlobalSearchOption(v); }}
            getOptionLabel={(opt) => typeof opt === 'string' ? opt : (opt?.label || '')}
            isOptionEqualToValue={(a, b) => a?.id === b?.id}
            groupBy={(opt) => opt.kind === 'aftercare' ? 'AfterCare Sites' : 'Projects (all statuses)'}
            autoHighlight
            noOptionsText={(globalSearch || '').trim().length < 2 ? 'Type at least 2 characters…' : 'No matches'}
            slotProps={{ paper: { sx: { minWidth: 480 } } }}
            renderOption={(props, opt) => {
              const isAftercare = opt.kind === 'aftercare';
              const statusChip = !isAftercare && opt.status ? opt.status : null;
              return (
                <Box component="li" {...props} key={opt.id} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, py: 1 }}>
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>{opt.label}</Typography>
                      {statusChip && (
                        <Chip label={statusChip} size="small" variant="outlined"
                          color={statusChip === 'active' ? 'success' : statusChip === 'completed' ? 'default' : statusChip === 'cancelled' ? 'error' : 'warning'}
                          sx={{ height: 18, fontSize: '0.65rem' }} />
                      )}
                      {isAftercare && (
                        <Chip label="AfterCare" size="small" variant="outlined" color="warning" sx={{ height: 18, fontSize: '0.65rem' }} />
                      )}
                    </Box>
                    {opt.site_address && (
                      <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary', mt: 0.25, whiteSpace: 'normal', lineHeight: 1.25 }}>
                        📍 {opt.site_address}
                      </Typography>
                    )}
                    {!isAftercare && opt.ar_number && (
                      <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block', mt: 0.25 }}>
                        AR {opt.ar_number}
                        {opt.project_name && opt.project_name !== opt.client_name ? ` • ${opt.project_name}` : ''}
                      </Typography>
                    )}
                  </Box>
                </Box>
              );
            }}
            renderInput={(params) => {
              const { InputProps = {}, inputProps = {}, ...rest } = params;
              return (
                <TextField
                  {...rest}
                  inputProps={inputProps}
                  placeholder="Search every project ever — name, address, job #, AR, client…"
                  variant="outlined"
                  InputProps={{
                    ...InputProps,
                    startAdornment: (
                      <Box sx={{ pl: 0.5, pr: 0.5, display: 'flex', alignItems: 'center', color: 'text.secondary' }}>
                        <SearchIcon fontSize="small" />
                      </Box>
                    ),
                    endAdornment: (
                      <>
                        {globalSearching ? <CircularProgress color="inherit" size={16} sx={{ mr: 1 }} /> : null}
                        {InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              );
            }}
          />
        </Box>

        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => openAddDialog(null, weekStart)}
            size="small"
          >
            Add Assignment
          </Button>
          <Button
            variant="outlined"
            size="small"
            onClick={() => navigate('/labor-progress')}
            sx={{ textTransform: 'none' }}
          >
            📊 Labor Progress
          </Button>
          {DISPATCH_EMAIL_IDS.has(user?.id) && (
            <Button
              variant="outlined"
              color="primary"
              size="small"
              disabled={sendingEmail}
              onClick={handleSendEmail}
              sx={{ textTransform: 'none' }}
            >
              {sendingEmail ? <CircularProgress size={16} /> : '✉️ Email Dispatch'}
            </Button>
          )}
        </Box>
      </Box>

      {/* ── Week nav + dept toggles bar ── */}
      <Paper elevation={0} sx={{ border: '1px solid #e0e0e0', borderRadius: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 2, py: 1.25, flexWrap: 'wrap' }}>
          {/* Week nav */}
          <IconButton size="small" onClick={goToPrevWeek}><ChevronLeftIcon /></IconButton>
          <Button size="small" variant="outlined" onClick={goToThisWeek} startIcon={<TodayIcon />} sx={{ minWidth: 100 }}>
            This Week
          </Button>
          <IconButton size="small" onClick={goToNextWeek}><ChevronRightIcon /></IconButton>
          <Typography variant="subtitle1" fontWeight="medium" sx={{ ml: 1 }}>
            {headerLabel}
          </Typography>
        </Box>

        {/* Department toggles */}
        <Box sx={{ display: 'flex', gap: 0.75, px: 2, pb: 1.25, flexWrap: 'wrap', alignItems: 'center' }}>
          <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5, fontWeight: 600, letterSpacing: 0.5 }}>
            DEPTS:
          </Typography>
          {allDeptNames.map((deptName) => (
            <DeptToggle
              key={deptName}
              name={deptName}
              color={DEPARTMENT_META[deptName].color}
              visible={!hiddenDepts.has(deptName)}
              onToggle={toggleDept}
            />
          ))}
          {hiddenDepts.size > 0 && (
            <Button
              size="small"
              variant="text"
              sx={{ fontSize: '0.7rem', px: 0.5, color: 'text.secondary', ml: 0.5 }}
              onClick={() => setHiddenDepts(new Set())}
            >
              Show all
            </Button>
          )}
        </Box>

        {/* Status legend */}
        <Box sx={{ display: 'flex', gap: 1.5, px: 2, pb: 1.25, flexWrap: 'wrap' }}>
          {Object.entries(STATUS_COLORS).map(([status, colors]) => (
            <Box key={status} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Box sx={{ width: 10, height: 10, borderRadius: '2px', bgcolor: colors.bg, border: `1px solid ${colors.border}` }} />
              <Typography variant="caption" sx={{ textTransform: 'capitalize', color: 'text.secondary' }}>
                {status}
              </Typography>
            </Box>
          ))}
        </Box>
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* ── Main grid ── */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Paper
          elevation={0}
          // overflow-x only: horizontal scroll stays inside the Paper (wide grid),
          // but vertical scroll bubbles up to the window so the sticky day-header
          // row resolves against the viewport and actually pins. Giving the Paper
          // overflow:auto on both axes made it the sticky containing block and
          // broke top-sticky. (Tom 2026-11-02)
          sx={{ border: '1px solid #e0e0e0', borderRadius: 2, overflowX: 'auto', overflowY: 'visible' }}
        >
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: `200px repeat(${WEEK_DAYS.length}, minmax(130px, 1fr))`,
              minWidth: 920,
            }}
          >
            {/* ── Sticky column header row ── */}
            {/* Corner cell — pinned top-left so day labels + TECHNICIAN stay visible
                while scrolling through department sections. Offset by 64px so
                it pins just under the fixed AppBar, not behind it. (Tom 2026-11-02) */}
            <Box
              sx={{
                bgcolor: '#1a237e',
                color: 'white',
                p: 1,
                display: 'flex',
                alignItems: 'center',
                borderRight: '1px solid rgba(255,255,255,0.15)',
                position: 'sticky',
                top: 64,
                left: 0,
                zIndex: 4,
              }}
            >
              <Typography variant="caption" fontWeight="bold">TECHNICIAN</Typography>
            </Box>

            {/* Day headers — sticky to top so days of the week scroll with the
                department sections underneath them (Tom 2026-11-02). */}
            {weekDates.map((date) => {
              const todayFlag = isToday(date);
              return (
                <Box
                  key={toDateString(date)}
                  sx={{
                    bgcolor: todayFlag ? '#283593' : '#1a237e',
                    color: 'white',
                    p: 1,
                    textAlign: 'center',
                    borderRight: '1px solid rgba(255,255,255,0.15)',
                    borderBottom: todayFlag ? '3px solid #ffeb3b' : 'none',
                    position: 'sticky',
                    top: 64,
                    zIndex: 3,
                  }}
                >
                  <Typography variant="caption" fontWeight="bold" display="block">
                    {format(date, 'EEE')}
                  </Typography>
                  <Typography variant="caption" sx={{ opacity: 0.85 }}>
                    {format(date, 'MMM d')}
                  </Typography>
                  {todayFlag && (
                    <Chip label="Today" size="small" sx={{ height: 14, fontSize: '0.6rem', bgcolor: '#ffeb3b', color: '#000', mt: 0.25 }} />
                  )}
                </Box>
              );
            })}

            {/* ── Department groups ── */}
            {groupedDepts.map((dept) => {
              const meta = DEPARTMENT_META[dept.name] || { color: '#607d8b' };
              const deptColor = meta.color;
              const isHidden = hiddenDepts.has(dept.name);

              return (
                <React.Fragment key={dept.name}>
                  {/* Department header row — spans full grid width */}
                  <Box
                    sx={{
                      gridColumn: '1 / -1',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      bgcolor: deptColor,
                      color: 'white',
                      px: 1.5,
                      py: 0.6,
                      borderTop: '2px solid rgba(0,0,0,0.08)',
                      cursor: 'pointer',
                      userSelect: 'none',
                      '&:hover': { filter: 'brightness(0.93)' },
                    }}
                    onClick={() => toggleDept(dept.name)}
                  >
                    <Typography variant="caption" fontWeight="bold" sx={{ letterSpacing: 0.8, fontSize: '0.72rem' }}>
                      {dept.name.toUpperCase()}
                    </Typography>
                    <Typography variant="caption" sx={{ opacity: 0.75, fontSize: '0.68rem' }}>
                      ({dept.techs.length})
                    </Typography>
                    <Box sx={{ flexGrow: 1 }} />
                    {isHidden
                      ? <ExpandMoreIcon sx={{ fontSize: 16, opacity: 0.85 }} />
                      : <ExpandLessIcon sx={{ fontSize: 16, opacity: 0.85 }} />
                    }
                  </Box>

                  {/* Technician rows (hidden when dept is collapsed) */}
                  {!isHidden && dept.techs.map((tech) => {
                    const rowBg = hexAlpha(deptColor, 0.04);
                    const deptBorderColor = deptColor;

                    return (
                      <React.Fragment key={tech.id}>
                        {/* Tech name cell */}
                        <Box
                          sx={{
                            bgcolor: rowBg,
                            borderTop: '1px solid #e8e8e8',
                            borderRight: '1px solid #e0e0e0',
                            borderLeft: `3px solid ${deptBorderColor}`,
                            p: 1,
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'center',
                            minHeight: 60,
                            position: 'sticky',
                            left: 0,
                            zIndex: 1,
                          }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
                            {tech.is_dept_head && (
                              <StarIcon sx={{ fontSize: 11, color: deptColor, flexShrink: 0, mt: '1px' }} />
                            )}
                            <Typography
                              variant="body2"
                              fontWeight={tech.is_dept_head ? 700 : 500}
                              noWrap
                              sx={{ color: tech.is_dept_head ? deptColor : 'text.primary' }}
                            >
                              {tech.name}
                            </Typography>
                          </Box>
                          <Typography variant="caption" color="text.secondary" noWrap sx={{ pl: tech.is_dept_head ? '15px' : 0 }}>
                            {tech.role}
                          </Typography>
                        </Box>

                        {/* Day cells */}
                        {weekDates.map((date) => {
                          const dateKey = toDateString(date);
                          const cellAssignments = getAssignmentsForCell(tech.id, date);
                          const todayFlag = isToday(date);

                          return (
                            <Box
                              key={dateKey}
                              onClick={() => openAddDialog(tech, date)}
                              sx={{
                                bgcolor: todayFlag ? '#fffde7' : rowBg,
                                borderTop: '1px solid #e8e8e8',
                                borderRight: '1px solid #e0e0e0',
                                p: 0.75,
                                minHeight: 60,
                                cursor: 'pointer',
                                transition: 'background-color 0.15s',
                                '&:hover': { bgcolor: todayFlag ? '#fff9c4' : hexAlpha(deptColor, 0.1) },
                              }}
                            >
                              {cellAssignments.map((a) => (
                                <AssignmentChip
                                  key={a.id}
                                  assignment={a}
                                  onEdit={openEditDialog}
                                  onConfirm={handleConfirm}
                                />
                              ))}
                            </Box>
                          );
                        })}
                      </React.Fragment>
                    );
                  })}
                </React.Fragment>
              );
            })}

            {/* ── Unassigned row ── */}
            {(() => {
              const displayedTechIds = new Set(technicians.map((t) => t.id));
              const unassigned = assignments.filter(
                (a) => !a.technician_id || !displayedTechIds.has(a.technician_id)
              );
              if (unassigned.length === 0) return null;

              const byDate = new Map();
              unassigned.forEach((a) => {
                const dk = a.assignment_date ? a.assignment_date.slice(0, 10) : '';
                if (!byDate.has(dk)) byDate.set(dk, []);
                byDate.get(dk).push(a);
              });

              return (
                <React.Fragment key="__unassigned__">
                  <Box
                    sx={{
                      bgcolor: '#fff3e0',
                      borderTop: '2px solid #e0e0e0',
                      borderRight: '1px solid #e0e0e0',
                      p: 1,
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center',
                      minHeight: 60,
                      position: 'sticky',
                      left: 0,
                      zIndex: 1,
                    }}
                  >
                    <Typography variant="body2" fontWeight="medium" color="text.secondary">
                      Unassigned
                    </Typography>
                  </Box>
                  {weekDates.map((date) => {
                    const dateKey = toDateString(date);
                    const cellAssignments = byDate.get(dateKey) || [];
                    return (
                      <Box
                        key={dateKey}
                        onClick={() => openAddDialog(null, date)}
                        sx={{
                          bgcolor: '#fff3e0',
                          borderTop: '2px solid #e0e0e0',
                          borderRight: '1px solid #e0e0e0',
                          p: 0.75,
                          minHeight: 60,
                          cursor: 'pointer',
                          '&:hover': { bgcolor: '#ffe0b2' },
                        }}
                      >
                        {cellAssignments.map((a) => (
                          <AssignmentChip
                            key={a.id}
                            assignment={a}
                            onEdit={openEditDialog}
                            onConfirm={handleConfirm}
                          />
                        ))}
                      </Box>
                    );
                  })}
                </React.Fragment>
              );
            })()}
          </Box>
        </Paper>
      )}

      {/* ── Assignment dialog ── */}
      <AssignmentDialog
        open={dialogOpen}
        onClose={handleDialogClose}
        onSave={handleSave}
        onDelete={handleDelete}
        initialData={editingAssignment}
        technicians={technicians}
        prefillTech={prefillTech}
        prefillDate={prefillDate}
        prefillTarget={prefillTarget}
      />
    </Box>
  );
};

export default Dispatch;
